import { isFunction } from 'lodash-es';
import {
  type SetStateAction,
  useCallback,
  useState,
  type Dispatch,
} from 'react';

import { useCallbackRef } from './useCallbackRef.js';

/** @internal */
export interface useControllableStateProps<T> {
  /** The controlled value to be used */
  value?: T;
  /** The default value to be used when uncontrolled*/
  defaultValue?: T | (() => T);
  /** The callback fired when the value changes */
  onChange?: (value: T) => void;
}

/**
 * @internal
 * React hook for using controlling component state.
 */
export function useControllableState<T>(props: useControllableStateProps<T>) {
  const { value: valueProp, defaultValue, onChange } = props;
  const onChangeProp = useCallbackRef(onChange);

  const [valueState, setValue] = useState(defaultValue as T);

  const isControlled = valueProp !== undefined;
  const value = isControlled ? (valueProp as T) : valueState;

  const updateValue = useCallback(
    (next: SetStateAction<T>) => {
      const nextValue = isFunction(next) ? next(value) : next;

      if (!isControlled) {
        setValue(nextValue);
      }

      onChangeProp(nextValue);
    },
    [isControlled, onChangeProp, value],
  );

  return [value, updateValue] as [T, Dispatch<SetStateAction<T>>];
}
